import React, { useState, useRef, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Search,
  SlidersHorizontal,
  Bell,
  Coins,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Menu,
  CreditCard,
  Gem,
} from "lucide-react";
import Logo from "../../../assets/Logo.svg";
import styles from "./FeedHeader.module.css";
import { useAppSelector, useAppDispatch } from "../../../redux/typedHooks";
import { UserInterface } from "../../../types/user";
import { logoutUser } from "../../../redux/modules/userSlice";
import { clearTokens } from "../../../utils/tokens";
import { queryClient } from "../../../query/queryClient";

interface FeedHeaderProps {
  onToggleMobileMenu?: () => void;
  onSearchChange?: (query: string) => void;
}

export default function FeedHeader({
  onToggleMobileMenu,
  onSearchChange,
}: FeedHeaderProps) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  const currentUser = useAppSelector<UserInterface | null>((state) => state.user.currentUser);
  const credits = (currentUser as any)?.credits?.total ?? 2;

  const userAvatar =
    currentUser?.profileImg ||
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearTokens();
    queryClient.clear();
    dispatch(logoutUser());
    history.push("/login");
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        {/* ── Left: Logo + Search Bar ── */}
        <div className={styles.leftSection}>
          <button
            className={styles.hamburgerBtn}
            onClick={onToggleMobileMenu}
            aria-label="Toggle navigation"
          >
            <Menu size={22} />
          </button>

          <Link to="/" className={styles.logoLink}>
            <img src={Logo} alt="SugarCribs" className={styles.logo} />
          </Link>

          {/* Search Box */}
          <div className={styles.searchBox}>
            <Search size={17} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search"
              className={styles.searchInput}
              value={searchQuery}
              onChange={handleSearch}
            />
            <button className={styles.filterIconBtn} aria-label="Filter options">
              <SlidersHorizontal size={16} />
            </button>
          </div>
        </div>

        {/* ── Right: Credits + Go Premium + Notifications + My Profile ── */}
        <div className={styles.rightSection}>
          {/* Credits Display */}
          <div
            className={styles.creditsContainer}
            onClick={() => history.push("/settings/monetization")}
            title="Credits Balance"
          >
            <span className={styles.creditsLabel}>Credits</span>
            <div className={styles.creditsRow}>
              <Coins size={18} className={styles.creditsCoinIcon} />
              <span className={styles.creditsAmount}>{credits}</span>
            </div>
          </div>

          {/* Go Premium Button */}
          <button
            className={styles.goPremiumBtn}
            onClick={() => history.push("/member-onboarding-step?step=6")}
          >
            <Gem size={17} className={styles.goPremiumIcon} />
            <span>Go Premium</span>
          </button>

          {/* Notification Bell */}
          <button
            className={styles.notifBtn}
            onClick={() => history.push("/notifications")}
            title="Notifications"
            aria-label="Notifications"
          >
            <Bell size={18} />
            <span className={styles.notifDot} />
          </button>

          {/* My Profile Dropdown */}
          <div className={styles.profileMenuWrapper} ref={menuRef}>
            <button
              className={styles.profileTrigger}
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label="My Profile menu"
            >
              <img
                src={userAvatar}
                alt="Profile avatar"
                className={styles.avatar}
              />
              <span className={styles.profileName}>My Profile</span>
              <ChevronDown
                size={14}
                className={`${styles.chevronIcon} ${
                  isMenuOpen ? styles.chevronIconOpen : ""
                }`}
              />
            </button>

            {/* Dropdown Options */}
            {isMenuOpen && (
              <div className={styles.dropdownMenu}>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setIsMenuOpen(false);
                    history.push(
                      currentUser?.username
                        ? `/${currentUser.username}`
                        : "/profile"
                    );
                  }}
                >
                  <User size={16} />
                  <span>View Profile</span>
                </button>

                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setIsMenuOpen(false);
                    history.push("/the-vault");
                  }}
                >
                  <Gem size={16} color="#fe5a97" />
                  <span>The Vault</span>
                </button>

                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setIsMenuOpen(false);
                    history.push("/settings/billing");
                  }}
                >
                  <CreditCard size={16} />
                  <span>Subscriptions & Billing</span>
                </button>

                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setIsMenuOpen(false);
                    history.push("/settings/personal-information");
                  }}
                >
                  <Settings size={16} />
                  <span>Account Settings</span>
                </button>

                <div className={styles.dropdownDivider} />

                <button
                  className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
